/**
 * Compute the geographic extent of the markers a search response produces,
 * for fitting the map to a location search's results.
 */

function isLatLngPair(value) {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    Number.isFinite(value[0]) &&
    Number.isFinite(value[1])
  );
}

// Extend the extent with every [lat, lng] pair found in an arbitrarily nested
// latlng structure (point, polygon rings, multipolygon, tile bounds).
function extendWithLatLngs(extent, value) {
  if (isLatLngPair(value)) {
    const [lat, lng] = value;
    extent.latMin = Math.min(extent.latMin, lat);
    extent.latMax = Math.max(extent.latMax, lat);
    extent.lngMin = Math.min(extent.lngMin, lng);
    extent.lngMax = Math.max(extent.lngMax, lng);
  } else if (Array.isArray(value)) {
    value.forEach(item => extendWithLatLngs(extent, item));
  }
}

/**
 * Compute the extent of the map markers for a response.
 *
 * Prefers cluster tile bounds when the response is clustered (zoomed out the
 * API returns geotile clusters and the records carry no geometry); otherwise
 * unions every individual marker's point and/or shape.
 *
 * @param {Array} individualMarkers - MapView markers: { position: [lat,lng]|null, shape: nested latlngs|null }
 * @param {Array} geotileMarkers - MapView cluster markers: { bounds: [[latMin,lngMin],[latMax,lngMax]] }
 * @returns {[[number, number], [number, number]] | null} [[latMin,lngMin],[latMax,lngMax]], or null when nothing has geometry
 */
/**
 * Compute the extent of a bare latlng structure (e.g. a parsed geoShape).
 *
 * @param {Array | null} latLngs - Nested latlng structure from parseGeoShape
 * @returns {[[number, number], [number, number]] | null}
 */
export function computeLatLngsExtent(latLngs) {
  if (!latLngs) return null;
  return computeMarkersExtent([{ position: null, shape: latLngs }], []);
}

export function computeMarkersExtent(individualMarkers, geotileMarkers) {
  const extent = {
    latMin: Infinity,
    latMax: -Infinity,
    lngMin: Infinity,
    lngMax: -Infinity,
  };

  if (Array.isArray(geotileMarkers) && geotileMarkers.length > 0) {
    geotileMarkers.forEach(tile => extendWithLatLngs(extent, tile.bounds));
  } else if (Array.isArray(individualMarkers)) {
    individualMarkers.forEach(marker => {
      extendWithLatLngs(extent, marker.position);
      extendWithLatLngs(extent, marker.shape);
    });
  }

  if (!Number.isFinite(extent.latMin) || !Number.isFinite(extent.lngMin)) {
    return null;
  }

  return [
    [extent.latMin, extent.lngMin],
    [extent.latMax, extent.lngMax],
  ];
}
